import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { ref, getDownloadURL, uploadString } from 'firebase/storage';
import { storage } from '../firebase';
import { rowMatchesFilters as rowMatchesFiltersUtil } from './filterUtils';
import {
  getEventSummaries,
  calculateHeuristicSpans,
  parseJsonl,
  safeParseJson,
  filterMouseClicks,
  attachClickImageFileNames,
  determinePlatform,
} from '@appesteem/aeshared-js/loadwatch';
import { getBucketLabel } from './bucketLabels';

// Precomputed event-summary cache written next to the other run artifacts (by
// autoinspect-runner, backfilled here).
const EVENT_SUMMARIES_FILE = 'event_summaries.json';
// Row count at/above which a log counts as "large": the summary compute switches to the
// cooperative idle loop, and it becomes worth reading/writing the precomputed cache. Below
// this the compute is effectively instant, so both the idle loop and a cache round-trip are skipped.
const LARGE_LOG_ROW_THRESHOLD = 30000;
// Bound the cache read so a stalled GCS response falls back to local compute instead of hanging.
const EVENT_SUMMARIES_FETCH_TIMEOUT_MS = 15000;

function eventSummariesRef(blobPath) {
  return ref(storage, `${blobPath}/${EVENT_SUMMARIES_FILE}`);
}

// Read the precomputed heuristic summary. Returns `{ summaries, platform }`, or null when
// missing / unreadable / malformed / slow (the caller then computes locally). `platform`
// is the view the cache was computed with; a cache written before the field existed is
// a Windows view (the only one that existed then) and comes back as 'windows'.
async function fetchCachedEventSummaries(blobPath) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EVENT_SUMMARIES_FETCH_TIMEOUT_MS);
  try {
    const url = await getDownloadURL(eventSummariesRef(blobPath));
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    const data = await res.json();
    if (data && typeof data === 'object' && data.summaries && typeof data.summaries === 'object') {
      const platform = typeof data.platform === 'string' && data.platform.toLowerCase() === 'macos' ? 'macos' : 'windows';
      return { summaries: data.summaries, platform };
    }
    return null;
  } catch {
    // storage/object-not-found (no cache yet), a timeout abort, or any read/parse failure
    // → treat as a miss and compute locally.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Best-effort write of the heuristic summary. Never throws: transient failures are
// retried by the Firebase SDK; storage/unauthorized (non-admin) is terminal. Display has
// already happened, so a failed persist is invisible to the user.
async function uploadCachedEventSummaries(blobPath, summaries, platform) {
  try {
    await uploadString(
      eventSummariesRef(blobPath),
      JSON.stringify({
        spansMode: 'heuristic',
        platform,
        generatedBy: 'aeinspect',
        generatedAt: new Date().toISOString(),
        summaries,
      }),
      'raw',
      { contentType: 'application/json' },
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('[event-summaries] cache upload skipped:', err?.code || err?.message || err);
  }
}

// Debounce utility for performance optimization
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

/**
 * Extract a safe base filename from a video URL (Firebase Storage or regular URL)
 * Decodes URL-encoded characters and replaces slashes with underscores
 */
export function getVideoBaseName(videoSrc) {
  if (!videoSrc) return 'video';
  try {
    // Decode URL-encoded characters
    const decodedSrc = decodeURIComponent(videoSrc);
    // Extract the storage path (after /o/ in Firebase URLs)
    const match = decodedSrc.match(/\/o\/([^?]+)/);
    const path = match ? match[1] : decodedSrc.split('/').pop() || 'video';
    // Replace slashes with underscores, remove extension
    return path.replace(/\//g, '_').replace(/\.[^/.]+$/, '');
  } catch {
    return 'video';
  }
}

/**
 * DataProvider loads, parses, sorts, filters, and buckets the log data.
 * It provides all processed data and state to children via context.
 */
export function DataProvider({ filePath, children, initialSeekTimeSeconds = null, blobPath = null }) {
  const [rawRows, setRawRows] = useState([]);
  const [sortBy, setSortBy] = useState('ActionTime');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showOnlyCurrentSecond, setShowOnlyCurrentSecond] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [seekTo, setSeekTo] = useState(null);

  // Apply initial video timestamp from URL (e.g. ?t=120) once on mount
  useEffect(() => {
    if (initialSeekTimeSeconds != null && typeof initialSeekTimeSeconds === 'number' && !Number.isNaN(initialSeekTimeSeconds)) {
      setSeekTo(initialSeekTimeSeconds);
    }
  }, [initialSeekTimeSeconds]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState([]); // [{ column, op, value, mode }]
  const [hints, setHints] = useState([]);
  const [videoRef, setVideoRef] = useState({});
  const [aiVideoRef, setAiVideoRef] = useState({});
  const [files, setFiles] = useState([]);
  const [copycerts, setCerts] = useState([]);
  const [bitsinfo, setBitsInfoState] = useState([]);
  const [storyitems, setItems] = useState([]);
  const [mouseClickRows, setMouseClickRows] = useState([]);
  const [autoinspectData, setAutoinspectData] = useState(null);
  const [reviewedReport, setReviewedReport] = useState(null);
  const [installerFileInfo, setInstallerFileInfoState] = useState(null);
  // metadata.json of the run (Executable, Platform, PlatformVersion, …), loaded by
  // VisualStoryTimeline's MetadataLoader. null until fetched / when the run has none.
  const [metadata, setMetadataState] = useState(null);
  // Loading states for additional files
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingCopycerts, setLoadingCopycerts] = useState(false);
  const [loadingBitsinfo, setLoadingBitsinfo] = useState(false);
  const [loadingStoryitems, setLoadingStoryitems] = useState(false);
  const [loadingAutoinspect, setLoadingAutoinspect] = useState(false);
  const [loadingReviewedReport, setLoadingReviewedReport] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [showTrimControls, setShowTrimControls] = useState(false);
  const [trimData, setTrimData] = useState(null);
  const [showEventDetailsPanel, setShowEventDetailsPanel] = useState(false);
  // Selected scenario from ScenarioSpans
  const [selectedScenario, setSelectedScenario] = useState(null);
  /// save the spans too
  const [spans, setSpans] = useState([]);
  // Gate expensive derived computations for huge logs
  const [summariesEnabled, setSummariesEnabled] = useState(true);
  const [hugeLogMode, setHugeLogMode] = useState(false);
  // Event summaries state (computed async for large datasets)
  const [eventSummariesData, setEventSummariesData] = useState({});
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [summariesProgress, setSummariesProgress] = useState({ current: 0, total: 0, section: '' });
  // Recompute button: bump the nonce to force a fresh compute (bypassing the cache).
  const [recomputeNonce, setRecomputeNonce] = useState(0);
  const lastRecomputeNonce = useRef(0);
  const recompute = useCallback(() => setRecomputeNonce((n) => n + 1), []);
  
  // New loading progress state
  const [loadingProgress, setLoadingProgress] = useState({
    phase: 'idle', // 'downloading', 'parsing', 'processing', 'complete'
    percentage: 0,
    message: '',
    fileSize: 0,
    processedRows: 0,
    totalRows: 0,
    isLargeFile: false
  });

  // Debounce sort and filter operations to improve performance
  const debouncedSortBy = useDebounce(sortBy, 100);
  const debouncedSortDir = useDebounce(sortDir, 100);
  const debouncedFilters = useDebounce(filters, 200);

  const setHintsData = useCallback((hints) => { setHints(hints)}, []);
  const setVideoReference = useCallback((videoref) => { setVideoRef(videoref)}, []);
  const setAiVideoReference = useCallback((aivideoref) => { setAiVideoRef(aivideoref)}, []);
  const setFileInfo = useCallback((files) => { setFiles(files)}, []);
  const setCopyCerts = useCallback((copycerts) => { setCerts(copycerts)}, []);
  const setBitsInfo = useCallback((bitsinfo) => { setBitsInfoState(bitsinfo)}, []);
  const setStoryItems = useCallback((storyitems) => { setItems(storyitems)}, []);
  const setAutoinspectDataCallback = useCallback((data) => { setAutoinspectData(data); }, []);
  const setReviewedReportCallback = useCallback((data) => { setReviewedReport(data); }, []);
  const setInstallerFileInfo = useCallback((data) => { setInstallerFileInfoState(data); }, []);
  const setMetadata = useCallback((data) => { setMetadataState(data && typeof data === 'object' ? data : null); }, []);

  // The platform that WROTE this run ("windows" | "macos"): metadata.json's Platform tag;
  // no tag (every run before the macOS work) means Windows. Every platform-specific
  // decision in the UI keys off this — never off the viewer's OS.
  const platform = useMemo(() => determinePlatform(metadata), [metadata]);
  // Loading state setters
  const setLoadingFilesCallback = useCallback((loading) => { setLoadingFiles(loading); }, []);
  const setLoadingCopycertsCallback = useCallback((loading) => { setLoadingCopycerts(loading); }, []);
  const setLoadingBitsinfoCallback = useCallback((loading) => { setLoadingBitsinfo(loading); }, []);
  const setLoadingStoryitemsCallback = useCallback((loading) => { setLoadingStoryitems(loading); }, []);
  const setLoadingAutoinspectCallback = useCallback((loading) => { setLoadingAutoinspect(loading); }, []);
  const setLoadingReviewedReportCallback = useCallback((loading) => { setLoadingReviewedReport(loading); }, []);

  // Add/remove filter helpers
  const addFilter = useCallback((filter) => {
    setFilters(prev => {
      // Prevent duplicate: same column, op, value, mode
      if (prev.some(f => f.column === filter.column && f.op === filter.op && f.value === filter.value && f.mode === filter.mode)) {
        return prev;
      }
      return [...prev, filter];
    });
  }, []);
  
  const removeFilter = useCallback((idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
  }, []);
  
  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  // Use the utility functions for filtering
  const rowMatchesFilters = useCallback((row, filters) => {
    return rowMatchesFiltersUtil(row, filters);
  }, []);

  // Load and parse file with detailed progress tracking
  useEffect(() => {
    if (!filePath) return;
    
    // Clear previous data immediately when switching files
    setRawRows([]);
    setSelectedIdx(null);
    setError(null);
    setSpans([]);
    setFiles([]);
    setItems([]);
    setCerts([]);
    setBitsInfoState([]);
    setAutoinspectData(null);
    setMouseClickRows([]);
    setCurrentVideoTime(0);
    setSelectedScenario(null);
    setSummariesEnabled(true);
    setHugeLogMode(false);
    // Clear loading states
    setLoadingFiles(false);
    setLoadingCopycerts(false);
    setLoadingBitsinfo(false);
    setLoadingStoryitems(false);
    setLoadingAutoinspect(false);
    
    setLoading(true);
    setLoadingProgress({
      phase: 'downloading',
      percentage: 0,
      message: 'Downloading file...',
      fileSize: 0,
      processedRows: 0,
      totalRows: 0,
      isLargeFile: false
    });

    const abortController = new AbortController();
    const { signal } = abortController;

    const parseJsonlStream = async (res) => {
      const contentLength = res.headers.get('content-length');
      const totalBytes = contentLength ? Number.parseInt(contentLength, 10) : 0;
      const isLargeFile = totalBytes > 10_000_000; // 10MB heuristic

      if (!res.body || !res.body.getReader) {
        // Fallback (older browsers or non-streaming response)
        setLoadingProgress(prev => ({
          ...prev,
          phase: 'parsing',
          message: 'Loading file (non-streaming)...'
        }));
        const text = await res.text();
        const rows = parseJsonl(text);
        return { rows };
      }

      setLoadingProgress(prev => ({
        ...prev,
        fileSize: totalBytes || prev.fileSize,
        isLargeFile,
        message: isLargeFile && totalBytes
          ? `Streaming large file (${(totalBytes / 1024 / 1024).toFixed(1)}MB)...`
          : 'Streaming file...'
      }));

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      let buffered = '';
      let bytesRead = 0;
      let processedLines = 0;
      const rows = [];

      setLoadingProgress(prev => ({
        ...prev,
        phase: 'parsing',
        percentage: 0,
        processedRows: 0,
        totalRows: 0,
        message: 'Parsing stream...'
      }));

      let lastProgressTime = Date.now();
      const reportProgress = (percentage, processedLines, bytesRead) => {
        lastProgressTime = Date.now();
        setLoadingProgress(prev => ({
          ...prev,
          percentage,
          processedRows: processedLines,
          message: totalBytes
            ? `Parsing... ${(bytesRead / 1024 / 1024).toFixed(1)}MB/${(totalBytes / 1024 / 1024).toFixed(1)}MB (${percentage}%) ${processedLines.toLocaleString()} events`
            : `Parsing... ${(bytesRead / 1024 / 1024).toFixed(1)}MB ${processedLines.toLocaleString()} events`
        }));
      };

      // Report initial progress immediately
      reportProgress(0, 0, 0);

      while (true) {
        let readResult;
        try {
          readResult = await reader.read();
        } catch (readErr) {
          console.error('[DataProvider] Stream read error:', readErr);
          throw new Error(`Stream read failed: ${readErr.message}`);
        }
        
        const { done, value } = readResult;
        if (done) break;

        bytesRead += value?.byteLength || 0;
        buffered += decoder.decode(value, { stream: true });

        // Process complete lines only; keep remainder in buffer
        const lastNewline = buffered.lastIndexOf('\n');
        if (lastNewline === -1) {
          // No full line yet - still report progress to show stream is active
          const now = Date.now();
          if (now - lastProgressTime > 500) {
            const percentage = totalBytes ? Math.min(99, Math.round((bytesRead / totalBytes) * 100)) : 0;
            reportProgress(percentage, processedLines, bytesRead);
          }
          continue;
        }

        const chunkText = buffered.slice(0, lastNewline);
        buffered = buffered.slice(lastNewline + 1);

        const lines = chunkText.split('\n');
        for (const line of lines) {
          if (!line) continue;
          try {
            const parsed = safeParseJson(line);
            if (parsed === null) continue;
            rows.push(parsed);
            processedLines++;
          } catch {
            // Defensive: skip any line that fails for any reason.
          }
        }

        // Progress update - more frequent at start, then every 5000 lines or 500ms
        const now = Date.now();
        const shouldReport = 
          processedLines < 1000 ? processedLines % 100 === 0 :
          processedLines < 10000 ? processedLines % 1000 === 0 :
          processedLines % 5000 === 0 ||
          now - lastProgressTime > 500;
          
        if (shouldReport) {
          const percentage = totalBytes ? Math.min(99, Math.round((bytesRead / totalBytes) * 100)) : 0;
          reportProgress(percentage, processedLines, bytesRead);
        }
      }

      // Flush remaining buffer
      buffered += decoder.decode();
      if (buffered) {
        const lines = buffered.split('\n');
        for (const line of lines) {
          if (!line) continue;
          try {
            const parsed = safeParseJson(line);
            if (parsed === null) continue;
            rows.push(parsed);
            processedLines++;
          } catch {
            // Defensive: skip any line that fails for any reason.
          }
        }
      }

      setLoadingProgress(prev => ({
        ...prev,
        phase: 'complete',
        percentage: 100,
        processedRows: processedLines,
        totalRows: processedLines,
        message: `Loaded ${processedLines.toLocaleString()} events`
      }));

      return { rows };
    };

    (async () => {
      try {
        setLoadingProgress(prev => ({
          ...prev,
          phase: 'downloading',
          percentage: 0,
          message: 'Downloading file...'
        }));

        const res = await fetch(filePath, { signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const { rows } = await parseJsonlStream(res);

        if (signal.aborted) return;
        setRawRows(rows);
        // Decide huge-log mode based on content-length if available, otherwise based on row count
        const sizeFromHeader = res.headers.get('content-length');
        const headerBytes = sizeFromHeader ? Number.parseInt(sizeFromHeader, 10) : 0;
        // Trigger earlier: 20MB or 100k events tends to be enough to cause UI stalls if we do full scans/sorts.
        const huge = headerBytes >= 20_000_000 || rows.length >= 100_000;
        setHugeLogMode(!!huge);
        // Default behavior: if huge, don't compute summaries until the user opens them
        setSummariesEnabled(!huge);
        setMouseClickRows(filterMouseClicks(rows));
        setLoading(false);

        setTimeout(() => {
          setLoadingProgress(prev => ({ ...prev, phase: 'idle' }));
        }, 2000);
      } catch (err) {
        if (signal.aborted) return;
        setError(err);
        setLoading(false);
        setLoadingProgress(prev => ({ ...prev, phase: 'idle' }));
        console.error('Loading error:', err);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [filePath]);

  // Calculate heuristic spans when data is loaded and no manual spans exist
  useEffect(() => {
    // Only calculate if we have data
    if (rawRows.length === 0) {
      // If we have no rawRows, clear heuristic spans (but preserve manual spans)
      setSpans(prevSpans => {
        if (prevSpans.length > 0 && prevSpans.every(s => s.isHeuristic)) {
          return [];
        }
        return prevSpans;
      });
      return;
    }

    // Check if manual spans exist (from annotations) - use functional update to get current spans
    setSpans(prevSpans => {
      // If manual spans exist (from annotations), don't calculate heuristic spans
      if (prevSpans.length > 0) {
        const hasManualSpans = prevSpans.some(s => !s.isHeuristic);
        if (hasManualSpans) {
          // Manual spans exist, don't override
          return prevSpans;
        }
        // If all spans are heuristic, we can recalculate (data might have changed)
      }

      // Calculate heuristic spans (storyitems and files may be empty arrays, which is fine)
      const heuristicSpans = calculateHeuristicSpans(rawRows, storyitems, files, { platform });
      
      // Always update spans if we have rawRows
      // This ensures spans are recalculated when files/storyitems load
      if (heuristicSpans.length > 0) {
        return heuristicSpans;
      } else {
        // Clear existing heuristic spans if calculation returns empty
        // This handles the case where old heuristic spans exist but new calculation returns empty
        if (prevSpans.length > 0 && prevSpans.every(s => s.isHeuristic)) {
          return [];
        }
        return prevSpans;
      }
    });
  }, [rawRows, storyitems, files, platform, setSpans]);

  // Filtering (applied to rawRows, before sorting) - now debounced
  const filteredRows = useMemo(() => {
    if (!debouncedFilters.length) return rawRows;
    return rawRows.filter(row => rowMatchesFilters(row, debouncedFilters));
  }, [rawRows, debouncedFilters, rowMatchesFilters]);

  // Sorting (pure, memoized) - now debounced
  const sortedRows = useMemo(() => {
    if (!filteredRows.length) return [];
    const rows = [...filteredRows];
    if (!debouncedSortBy) return rows;
    if (hugeLogMode) return filteredRows;
    
    return rows.sort((a, b) => {
      const aVal = a[debouncedSortBy];
      const bVal = b[debouncedSortBy];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return debouncedSortDir === 'asc' ? -1 : 1;
      if (bVal == null) return debouncedSortDir === 'asc' ? 1 : -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return debouncedSortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return debouncedSortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredRows, debouncedSortBy, debouncedSortDir, hugeLogMode]);

  // Display rows (filtered and sorted, now using ms precision)
  const displayRows = useMemo(() => {
    if (!showOnlyCurrentSecond || typeof currentVideoTime !== 'number' || isNaN(currentVideoTime)) {
      return sortedRows;
    }
    // Use a window of ±200ms around currentVideoTime
    const windowMs = 200;
    return sortedRows.filter(row => {
      if (typeof row.VideoOffset !== 'number') return false;
      return Math.abs(row.VideoOffset - currentVideoTime * 1000) <= windowMs;
    });
  }, [sortedRows, showOnlyCurrentSecond, currentVideoTime]);

  // Bucketing for sparkline (memoized) - optimized for large datasets
  const sparklineData = useMemo(() => {
    // [{ second, count }]
    const counts = new Map(); // Use Map for better performance
    filteredRows.forEach(ev => {
      if (typeof ev.VideoOffset === 'number') {
        const sec = Math.floor(ev.VideoOffset / 1000);
        counts.set(sec, (counts.get(sec) || 0) + 1);
      }
    });
    
    if (counts.size === 0) return [];
    
    const allSecs = Array.from(counts.keys());
    let minSec = Infinity;
    let maxSec = -Infinity;
    for (const s of allSecs) {
      if (s < minSec) minSec = s;
      if (s > maxSec) maxSec = s;
    }
    if (!Number.isFinite(minSec) || !Number.isFinite(maxSec)) return [];
    const data = [];
    
    for (let s = minSec; s <= maxSec; ++s) {
      data.push({ second: s, count: counts.get(s) || 0 });
    }
    return data;
  }, [filteredRows]);

  // Selected event
  const selectedEvent = useMemo(() => {
    if (selectedIdx == null || selectedIdx < 0 || selectedIdx >= displayRows.length) return null;
    return displayRows[selectedIdx];
  }, [displayRows, selectedIdx]);

  // Synchronize selectedIdx with video time when filtering by current ms
  useEffect(() => {
    if (!showOnlyCurrentSecond || typeof currentVideoTime !== 'number' || isNaN(currentVideoTime)) return;
    const windowMs = 200;
    const idx = displayRows.findIndex(row => {
      if (typeof row.VideoOffset !== 'number') return false;
      return Math.abs(row.VideoOffset - currentVideoTime * 1000) <= windowMs;
    });
    setSelectedIdx(idx >= 0 ? idx : null);
  }, [currentVideoTime, showOnlyCurrentSecond, displayRows]);

  // Click-image filename + enrichment come from aeshared-js/loadwatch.
  const mouseClickRowsWithImages = useMemo(
    () => attachClickImageFileNames(mouseClickRows),
    [mouseClickRows],
  );

  // Precompute click offsets for fast next/prev (O(log n) instead of O(n))
  const clickIndex = useMemo(() => {
    if (!Array.isArray(mouseClickRows) || mouseClickRows.length === 0) {
      return { offsets: [], events: [] };
    }
    // Assume rows are already roughly chronological; still sort to ensure correctness for binary search.
    const events = mouseClickRows
      .filter(e => typeof e?.VideoOffset === 'number' && Number.isFinite(e.VideoOffset))
      .slice()
      .sort((a, b) => a.VideoOffset - b.VideoOffset);
    const offsets = events.map(e => e.VideoOffset);
    return { offsets, events };
  }, [mouseClickRows]);

  const upperBound = (arr, x) => {
    // first index with arr[i] > x
    let lo = 0, hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid] <= x) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  const getNextClickTime = useCallback((videoOffsetTime) => {
    const { offsets } = clickIndex;
    if (!offsets.length || typeof videoOffsetTime !== 'number') return videoOffsetTime;
    const i = upperBound(offsets, videoOffsetTime);
    return i < offsets.length ? offsets[i] : videoOffsetTime;
  }, [clickIndex]);

  const getPrevClickTime = useCallback((videoOffsetTime) => {
    const { offsets } = clickIndex;
    if (!offsets.length || typeof videoOffsetTime !== 'number') return videoOffsetTime;
    const i = upperBound(offsets, videoOffsetTime) - 1;
    return i >= 0 ? offsets[i] : videoOffsetTime;
  }, [clickIndex]);

  const getNextClickEvent = useCallback((videoOffsetTime) => {
    const { offsets, events } = clickIndex;
    if (!offsets.length || typeof videoOffsetTime !== 'number') return null;
    const i = upperBound(offsets, videoOffsetTime);
    return i < events.length ? events[i] : null;
  }, [clickIndex]);

  const getPrevClickEvent = useCallback((videoOffsetTime) => {
    const { offsets, events } = clickIndex;
    if (!offsets.length || typeof videoOffsetTime !== 'number') return null;
    const i = upperBound(offsets, videoOffsetTime) - 1;
    return i >= 0 ? events[i] : null;
  }, [clickIndex]);

  // Compute event summaries
  const spansToUse = useMemo(() => {
    const allSpans = Array.isArray(spans) ? [...spans] : [];
    // Always add "all" span
    allSpans.push({ scenario: 'all', startActionMs: null, endActionMs: null });
    return allSpans;
  }, [spans]);

  // Async computation of event summaries. Large logs first try a precomputed cache
  // (event_summaries.json — the heuristic view, written by autoinspect-runner and
  // backfilled here) and only compute locally on a miss. Annotation (manual-span) views
  // always compute locally and never read/write the cache.
  useEffect(() => {
    if (!summariesEnabled) {
      setEventSummariesData({});
      setSummariesLoading(false);
      return;
    }
    // Clear any stale spinner left by a cancelled prior run: a large-log compute that was
    // interrupted mid-flight never reaches its own setSummariesLoading(false). The large-log
    // path below re-sets it; the fast paths (cache hit, small log) leave it off.
    setSummariesLoading(false);

    const rows = Array.isArray(rawRows) ? rawRows : [];
    const filesList = Array.isArray(files) ? files : [];
    const certsList = Array.isArray(copycerts) ? copycerts : [];
    const storyList = Array.isArray(storyitems) ? storyitems : [];
    const bitsList = Array.isArray(bitsinfo) ? bitsinfo : [];

    // Heuristic mode = no manual (annotation) spans active. The cache only holds the
    // heuristic view, so read/write it only in heuristic mode.
    const heuristicMode = !(Array.isArray(spans) && spans.some(s => !s.isHeuristic));
    const useCache = heuristicMode && !!blobPath && rows.length >= LARGE_LOG_ROW_THRESHOLD;
    // A Recompute-button press bumps the nonce → skip the cache read, force a compute.
    const forced = recomputeNonce !== lastRecomputeNonce.current;
    lastRecomputeNonce.current = recomputeNonce;

    let cancelled = false;
    let idleCallbackId = null;

    // Polyfill for requestIdleCallback
    const requestIdle = window.requestIdleCallback ||
      ((cb) => setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 50 }), 1));
    const cancelIdle = window.cancelIdleCallback || clearTimeout;

    // Helper that uses requestIdleCallback for cooperative scheduling
    const scheduleTask = (task) => {
      return new Promise((resolve) => {
        idleCallbackId = requestIdle((deadline) => {
          // Always run the task, but this gives the browser a chance to do other work first
          resolve(task());
        }, { timeout: 100 }); // Max wait 100ms before forcing execution
      });
    };

    // Helper to yield control back to browser more aggressively
    // The delay helps prevent "Page Unresponsive" by giving browser time to process events
    const yieldToBrowser = (delay = 50) => {
      return new Promise(resolve => {
        // Use requestAnimationFrame to ensure we yield at least one frame
        requestAnimationFrame(() => {
          // Then use setTimeout with a meaningful delay to let browser handle pending events
          setTimeout(resolve, delay);
        });
      });
    };

    // Compute the full summary locally: sync for small logs, cooperative idle loop for large.
    const computeSummaries = async () => {
      // For small datasets, compute synchronously on main thread
      if (rows.length < LARGE_LOG_ROW_THRESHOLD) {
        return getEventSummaries(rows, spansToUse, true, filesList, certsList, storyList, bitsList, { platform });
      }

      // For large datasets, use requestIdleCallback for better browser responsiveness
      setSummariesLoading(true);
      setSummariesProgress({ current: 0, total: 0, section: 'Initializing...' });

      const {
        buildUpidDict,
        filterInsignificantActions,
        getRegistryTreesWritten,
        getRegistryASEPsWritten,
        getDirectoriesWritten,
        getBitsFilesWritten,
        getDnsLookups,
        getTcpIps,
        getTaskLifeCycles,
        getCertificateLifeCycles,
        getFirewallRuleLifeCycles,
        getStartupLifeCycles,
        getUninstallerLifeCycles,
        getStartMenuLifeCycles,
        getDesktopItemLifeCycles,
        getServiceLifeCycles,
        getInterestingFileLifeCycles,
        getProcessLifeCycles
      } = await import(/* webpackChunkName: "loadwatch" */ '@appesteem/aeshared-js/loadwatch');

      if (cancelled) return null;

      const results = {};
      // Same 17 buckets, same order, same arguments as getEventSummaries — with the run's
      // platform threaded through every function that has a platform branch.
      const label = (key) => getBucketLabel(key, platform);
      const sections = [
        { key: 'upidDict', name: 'Building process index...', fn: () => buildUpidDict(rows) },
        { key: 'filteredEvents', name: 'Filtering events...', fn: (ctx) => filterInsignificantActions(rows, platform), isSlow: true },
        { key: 'RegistryTreesWritten', name: label('RegistryTreesWritten'), fn: (ctx) => getRegistryTreesWritten(ctx.filteredEvents, ctx.upidDict, spansToUse) },
        { key: 'RegistryASEPsWritten', name: label('RegistryASEPsWritten'), fn: (ctx) => getRegistryASEPsWritten(ctx.filteredEvents, ctx.upidDict, spansToUse) },
        { key: 'DirectoriesWritten', name: label('DirectoriesWritten'), fn: (ctx) => getDirectoriesWritten(ctx.filteredEvents, ctx.upidDict, spansToUse, filesList, false), isSlow: true },
        { key: 'InterestingDirectoriesWritten', name: label('InterestingDirectoriesWritten'), fn: (ctx) => getDirectoriesWritten(ctx.filteredEvents, ctx.upidDict, spansToUse, filesList, true) },
        { key: 'BitsFilesInfo', name: label('BitsFilesInfo'), fn: (ctx) => getBitsFilesWritten(ctx.filteredEvents, ctx.upidDict, spansToUse, bitsList) },
        { key: 'DnsLookups', name: label('DnsLookups'), fn: (ctx) => getDnsLookups(ctx.filteredEvents, ctx.upidDict, spansToUse) },
        { key: 'TcpIpConnections', name: label('TcpIpConnections'), fn: (ctx) => getTcpIps(ctx.filteredEvents, ctx.upidDict, spansToUse) },
        { key: 'TaskLifeCycles', name: label('TaskLifeCycles'), fn: (ctx) => getTaskLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true) },
        { key: 'CertificateLifeCycles', name: label('CertificateLifeCycles'), fn: (ctx) => getCertificateLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, certsList, platform) },
        { key: 'FirewallRuleLifeCycles', name: label('FirewallRuleLifeCycles'), fn: (ctx) => getFirewallRuleLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, platform) },
        { key: 'StartupLifeCycles', name: label('StartupLifeCycles'), fn: (ctx) => getStartupLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, platform) },
        { key: 'UninstallerLifeCycles', name: label('UninstallerLifeCycles'), fn: (ctx) => getUninstallerLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, platform) },
        { key: 'StartMenuItemLifeCycles', name: label('StartMenuItemLifeCycles'), fn: (ctx) => getStartMenuLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, platform) },
        { key: 'DesktopItemLifeCycles', name: label('DesktopItemLifeCycles'), fn: (ctx) => getDesktopItemLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, platform) },
        { key: 'ServiceLifeCycles', name: label('ServiceLifeCycles'), fn: (ctx) => getServiceLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, platform) },
        { key: 'InterestingFileInfo', name: `${label('InterestingFileInfo')} (processing...)`, fn: (ctx) => getInterestingFileLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, filesList, platform), isSlow: true },
        { key: 'ProcessLifeCycles', name: `${label('ProcessLifeCycles')} (processing...)`, fn: (ctx) => getProcessLifeCycles(ctx.filteredEvents, ctx.upidDict, spansToUse, true, storyList), isSlow: true },
      ];

      const context = {};
      for (let i = 0; i < sections.length; i++) {
        if (cancelled) return null;

        const section = sections[i];

        // Update progress before starting this section
        setSummariesProgress({
          current: i + 1,
          total: sections.length,
          section: section.name,
          isSlow: section.isSlow
        });

        // Yield to browser before heavy computation with longer delay for slow sections
        await yieldToBrowser(section.isSlow ? 150 : 50);

        if (cancelled) return null;

        try {
          // Schedule the heavy work using requestIdleCallback
          const result = await scheduleTask(() => section.fn(context));

          if (section.key === 'upidDict' || section.key === 'filteredEvents') {
            context[section.key] = result;
          } else {
            results[section.key] = result;
          }

          // Extra yield after slow sections to let browser recover
          if (section.isSlow) {
            await yieldToBrowser(200);
          }
        } catch (err) {
          console.error(`Error computing ${section.key}:`, err);
          results[section.key] = [];
        }
      }

      if (cancelled) return null;

      setSummariesLoading(false);
      setSummariesProgress({ current: 0, total: 0, section: '' });
      return results;
    };

    const loadOrComputeSummaries = async () => {
      // 1. Read-through cache (large heuristic-mode logs, unless a forced recompute).
      if (useCache && !forced) {
        const cached = await fetchCachedEventSummaries(blobPath);
        if (cancelled) return;
        // A cache is only a hit when it was computed for this run's platform. A macOS run
        // whose cache predates the platform field is a Windows view of mac data — recompute
        // and backfill it with the tag.
        if (cached && cached.platform === platform) {
          setEventSummariesData(cached.summaries);
          return;
        }
      }

      // 2. Compute locally.
      const summaries = await computeSummaries();
      if (cancelled || summaries == null) return;
      setEventSummariesData(summaries);

      // 3. Best-effort backfill (large heuristic-mode logs only).
      if (useCache) {
        uploadCachedEventSummaries(blobPath, summaries, platform);
      }
    };

    loadOrComputeSummaries();

    return () => {
      cancelled = true;
      if (idleCallbackId) {
        cancelIdle(idleCallbackId);
      }
    };
  }, [summariesEnabled, rawRows, spansToUse, files, copycerts, storyitems, bitsinfo, spans, blobPath, recomputeNonce, platform]);
  
  // Expose eventSummaries (the computed data)
  const eventSummaries = eventSummariesData;

  // Context value
  const value = {
    loading,
    loadingProgress,
    error,
    rawRows,
    allRows: rawRows, // <-- add this line to expose allRows (unfiltered events) to consumers
    sortedRows,
    displayRows,
    mouseClickRows,
    mouseClickRowsWithImages,
    getNextClickTime,
    getPrevClickTime,
    getNextClickEvent,
    getPrevClickEvent,
    sparklineData,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    selectedIdx,
    setSelectedIdx,
    selectedEvent,
    showOnlyCurrentSecond,
    setShowOnlyCurrentSecond,
    currentVideoTime,
    setCurrentVideoTime,
    seekTo,
    setSeekTo,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    hints,
    setHintsData,
    videoRef,
    setVideoReference,
    aiVideoRef,
    setAiVideoReference,
    files,
    setFileInfo,
    copycerts,
    setCopyCerts,
    bitsinfo,
    setBitsInfo,
    storyitems,
    setStoryItems,
    screenshotUrl,
    setScreenshotUrl,
    showTrimControls,
    setShowTrimControls,
    trimData,
    setTrimData,
    showEventDetailsPanel,
    setShowEventDetailsPanel,
    selectedScenario,
    setSelectedScenario,
    spans,
    setSpans,
    autoinspectData,
    setAutoinspectData: setAutoinspectDataCallback,
    reviewedReport,
    setReviewedReport: setReviewedReportCallback,
    installerFileInfo,
    setInstallerFileInfo,
    metadata,
    setMetadata,
    platform,
    eventSummaries,
    summariesEnabled,
    setSummariesEnabled,
    summariesLoading,
    summariesProgress,
    recompute,
    hugeLogMode,
    loadingFiles,
    loadingCopycerts,
    loadingBitsinfo,
    loadingStoryitems,
    loadingAutoinspect,
    loadingReviewedReport,
    setLoadingFiles: setLoadingFilesCallback,
    setLoadingCopycerts: setLoadingCopycertsCallback,
    setLoadingBitsinfo: setLoadingBitsinfoCallback,
    setLoadingStoryitems: setLoadingStoryitemsCallback,
    setLoadingAutoinspect: setLoadingAutoinspectCallback,
    setLoadingReviewedReport: setLoadingReviewedReportCallback,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

